import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AssetLogRecord,
  AssetLogsParams,
  AssetLogsService,
  UpdatedField,
  canViewAssetLogs
} from 'src/app/service/asset-logs.service';

@Component({
  selector: 'app-asset-history',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe],
  templateUrl: './asset-history.component.html',
  styleUrls: ['./asset-history.component.css']
})
export class AssetHistoryComponent implements OnInit, OnChanges {
  @Input() isUnified: boolean = true;
  @Input() assetType: 'All' | 'Inventory' | 'Workstation' = 'All';
  @Input() assetId?: string;
  @Input() assetName?: string;
  @Input() assetCode?: string;

  selectedTypeFilter: 'All' | 'Inventory' | 'Workstation' = 'All';

  logs: AssetLogRecord[] = [];
  isLoading = false;
  errorMessage = '';
  totalCounts = 0;
  page = 1;
  limit = 10;
  totalPages = 1;

  canView = false;

  // Selected image for modal preview
  selectedPreviewImage: string | null = null;

  // Set of expanded log record IDs
  expandedRecordIds = new Set<string>();

  constructor(public assetLogsService: AssetLogsService) {}

  ngOnInit(): void {
    this.canView = canViewAssetLogs();
    if (this.isUnified) {
      this.selectedTypeFilter = 'All';
    } else if (this.assetType && this.assetType !== 'All') {
      this.selectedTypeFilter = this.assetType;
    }
    if (this.canView) {
      this.fetchLogs();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assetType'] && !changes['assetType'].firstChange) {
      if (this.assetType) {
        this.selectedTypeFilter = this.assetType;
      }
      this.page = 1;
      this.fetchLogs();
    } else if (changes['isUnified'] || changes['assetId']) {
      if (!this.isUnified && this.assetType) {
        this.selectedTypeFilter = this.assetType;
      }
      this.page = 1;
      this.fetchLogs();
    }
  }

  fetchLogs(): void {
    if (!this.canView) return;

    // In per-asset mode, wait until assetId is provided
    if (!this.isUnified && !this.assetId) {
      this.logs = [];
      this.totalCounts = 0;
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const params: AssetLogsParams = {
      page: this.page,
      limit: this.limit
    };

    // For unified history view: DO NOT pass assetId in query parameters
    if (!this.isUnified && this.assetId) {
      params.assetId = this.assetId;
    }

    // Filter by asset type if selected
    if (this.selectedTypeFilter && this.selectedTypeFilter !== 'All') {
      params.assetType = this.selectedTypeFilter;
    }

    this.assetLogsService.getAssetLogs(params).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res?.data?.records) {
          this.logs = res.data.records || [];
          this.totalCounts = res.data.totalCounts ?? this.logs.length;
        } else if (Array.isArray(res?.data)) {
          this.logs = res.data;
          this.totalCounts = this.logs.length;
        } else if (Array.isArray((res as any)?.records)) {
          this.logs = (res as any).records;
          this.totalCounts = (res as any).totalCounts ?? this.logs.length;
        } else {
          this.logs = [];
          this.totalCounts = 0;
        }
        this.totalPages = Math.max(1, Math.ceil(this.totalCounts / this.limit));
      },
      error: (err) => {
        this.isLoading = false;
        if (err?.status === 400 && err?.error?.message) {
          this.errorMessage = err.error.message;
        } else {
          this.errorMessage = 'Failed to load activity logs.';
        }
      }
    });
  }

  setTypeFilter(type: 'All' | 'Inventory' | 'Workstation'): void {
    if (this.selectedTypeFilter === type) return;
    this.selectedTypeFilter = type;
    this.page = 1;
    this.fetchLogs();
  }

  onPageChange(newPage: number): void {
    if (newPage < 1 || newPage > this.totalPages || newPage === this.page) return;
    this.page = newPage;
    this.fetchLogs();
  }

  toggleExpand(recordId: string): void {
    if (this.expandedRecordIds.has(recordId)) {
      this.expandedRecordIds.delete(recordId);
    } else {
      this.expandedRecordIds.add(recordId);
    }
  }

  isExpanded(recordId: string): boolean {
    return this.expandedRecordIds.has(recordId);
  }

  openImagePreview(imgUrl: string): void {
    this.selectedPreviewImage = imgUrl;
  }

  closeImagePreview(): void {
    this.selectedPreviewImage = null;
  }

  getActionBadgeClass(action: string): string {
    const act = (action || '').toLowerCase();
    if (act.includes('create') || act.includes('approved')) {
      return 'badge-success';
    }
    if (act.includes('update') || act.includes('submitted')) {
      return 'badge-info';
    }
    if (act.includes('assign') && !act.includes('unassign')) {
      return 'badge-teal';
    }
    if (act.includes('extend') || act.includes('on hold')) {
      return 'badge-warning';
    }
    if (act.includes('forward') || act.includes('raised')) {
      return 'badge-purple';
    }
    if (act.includes('delete') || act.includes('reject') || act.includes('removed')) {
      return 'badge-danger';
    }
    if (act.includes('unassign') || act.includes('return')) {
      return 'badge-indigo';
    }
    return 'badge-default';
  }

  getUpdatedFields(record: AssetLogRecord): UpdatedField[] {
    const fields = record?.meta?.updatedFields;
    return Array.isArray(fields) ? fields : [];
  }

  getImages(record: AssetLogRecord): string[] {
    const imgs = record?.meta?.images;
    return Array.isArray(imgs) ? imgs : [];
  }

  hasExtraMeta(record: AssetLogRecord): boolean {
    const entries = this.assetLogsService.getGenericMetaEntries(record.meta, record.action);
    return entries.length > 0;
  }

  formatValue(val: any): string {
    if (val === null || val === undefined) return '—';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'object') {
      try {
        return JSON.stringify(val);
      } catch {
        return String(val);
      }
    }
    return String(val);
  }

  getInitials(name: string): string {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
}
